import React, { useRef } from 'react';

export function Modal({ title, onClose, children, footer, large }) {
  // Close on a click that both starts and ends on the backdrop. Checking only the
  // click target closed the modal (losing typed input) when a text selection was
  // dragged from inside the modal and released over the backdrop.
  const downOnOverlay = useRef(false);
  return (
    <div className="modal-overlay"
      onMouseDown={e => { downOnOverlay.current = e.target === e.currentTarget; }}
      onClick={e => { if (downOnOverlay.current && e.target === e.currentTarget) onClose(); downOnOverlay.current = false; }}>
      <div className={`modal ${large ? 'modal-lg' : ''}`}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="btn-icon" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`toast ${toast.type === 'error' ? 'error' : ''}`} role="status">
      <div className="toast-icon">{toast.type === 'error' ? '✕' : '✓'}</div>
      {toast.msg}
    </div>
  );
}

export function Badge({ type, children }) {
  return <span className={`badge b-${type}`}>{children}</span>;
}

export function TouchSteps({ current, status }) {
  return (
    <div className="touch-steps">
      {[1,2,3,4,5,6].map(n => (
        <div key={n} className={`ts ${n < current ? 'done' : n === current && status === 'overdue' ? 'overdue' : n === current ? 'current' : ''}`}>{n}</div>
      ))}
    </div>
  );
}

export function EmptyState({ icon, title, desc, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{ICON[icon] || icon || ICON.inbox}</div>
      <div className="empty-title">{title}</div>
      <div className="empty-desc">{desc}</div>
      {action}
    </div>
  );
}

export function statusBadge(status) {
  const map = {
    'New': 'i', 'Contacted': 'a', 'Engaged': 'a',
    'Meeting Scheduled': 'g', 'Meeting Held': 'g', 'Qualified': 'g',
    'Disqualified': 'r', 'Active': 'g', 'Paused': 'n',
    'Completed': 'i', 'Converted': 'g', 'Not Interested': 'r',
    'Scheduled': 'i', 'Hot': 'r', 'Warm': 'a', 'Cold': 'i',
    'High': 'r', 'Medium': 'a', 'Low': 'g',
    'Released': 'g', 'In Pool': 'v', 'Processed': 'g', 'Pool Only': 'v',
    'In Progress': 'a', 'Overdue': 'r', 'Due Today': 'a', 'Fresh': 'g', 'Red': 'r',
    'Running Low': 'a', 'Out': 'r', 'Healthy': 'g', 'Interested': 'g', 'Not Qualified': 'r',
  };
  return map[status] || 'n';
}

export function FormRow({ children }) {
  return <div className="form-grid-2">{children}</div>;
}

export function SectionDivider({ title }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0 12px' }}>
      <div style={{ fontFamily:'var(--fh)', fontSize:12.5, fontWeight:700, color:'var(--navy)', whiteSpace:'nowrap' }}>{title}</div>
      <div style={{ flex:1, height:1, background:'var(--border)' }} />
    </div>
  );
}

// Line icons used by stat tiles and empty states (same style as the sidebar).
const sv = (children) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
export const ICON = {
  phone: sv(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.35 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l.95-.95a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>),
  mail: sv(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>),
  calendar: sv(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>),
  alert: sv(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>),
  layers: sv(<><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>),
  send: sv(<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>),
  undo: sv(<><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></>),
  globe: sv(<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>),
  upload: sv(<><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></>),
  target: sv(<><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>),
  trend: sv(<><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>),
  users: sv(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>),
  inbox: sv(<><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>),
  check: sv(<polyline points="20 6 9 17 4 12"/>),
  search: sv(<><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>),
};

// Tiny trend line for a stat tile. `data` is a list of numbers, oldest first.
export function Sparkline({ data, color = 'var(--gold)', width = 84, height = 30 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1), min = Math.min(...data, 0);
  const span = max - min || 1, step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - 3 - ((v - min) / span) * (height - 6)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const id = 'sg' + Math.random().toString(36).slice(2, 8);
  return (
    <svg className="st-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity="0.28" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={`${line} L${width},${height} L0,${height} Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.6" fill={color} />
    </svg>
  );
}

const SPARK = { gold: 'var(--gold)', green: 'var(--green)', rose: 'var(--rose)', indigo: 'var(--indigo)', violet: 'var(--violet)', amber: 'var(--amber)' };

// A headline number: icon, value, change vs the previous period, and a trend line.
// `delta` is the change as a number (e.g. +3 or -12); `deltaPct` shows it as a %.
// `goodWhenDown` flips the colours for numbers where lower is better (e.g. overdue).
export function StatTile({ icon, tone = 'gold', label, value, suffix, delta, deltaLabel = 'vs last week', deltaPct, goodWhenDown, trend, foot, onClick }) {
  let dcls = 'flat', arrow = '→';
  if (delta > 0) { dcls = goodWhenDown ? 'down' : 'up'; arrow = '↑'; }
  if (delta < 0) { dcls = goodWhenDown ? 'up' : 'down'; arrow = '↓'; }
  const shown = delta == null ? null : deltaPct ? `${Math.abs(Math.round(delta))}%` : Math.abs(delta).toLocaleString();
  return (
    <div className={`stat-tile lift ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="st-top">
        <div className="st-label">{label}</div>
        <div className={`st-icon ${tone}`}>{ICON[icon] || ICON.trend}</div>
      </div>
      <div className="st-row">
        <div className="st-num">{value}{suffix && <small>{suffix}</small>}</div>
        <Sparkline data={trend} color={SPARK[tone]} />
      </div>
      <div className="st-foot">
        {shown != null && <span className={`st-delta ${dcls}`}>{arrow} {shown}</span>}
        {shown != null && <span>{deltaLabel}</span>}
        {shown == null && foot}
      </div>
    </div>
  );
}

// Shimmering placeholder shown while a page loads.
export function Skeleton({ tiles = 4, rows = 5 }) {
  return (
    <div className="skel-page" aria-busy="true" aria-label="Loading">
      {tiles > 0 && <div className="skel-row" style={{ gridTemplateColumns: `repeat(${tiles}, 1fr)` }}>
        {Array.from({ length: tiles }).map((_, i) => <div key={i} className="skel" style={{ height: 112, borderRadius: 14 }} />)}
      </div>}
      <div className="skel" style={{ height: 18, width: 220 }} />
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="skel" style={{ height: 44, opacity: 1 - i * 0.12 }} />)}
    </div>
  );
}

// Day-by-day counts for the last `days` days (oldest first) from rows with a date field.
export function dailySeries(rows, field, days = 7) {
  const out = new Array(days).fill(0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  (rows || []).forEach(r => {
    const v = r && r[field]; if (!v) return;
    const d = new Date(String(v).length === 10 ? v + 'T00:00:00' : v); d.setHours(0, 0, 0, 0);
    const back = Math.round((today - d) / 86400000);
    if (back >= 0 && back < days) out[days - 1 - back] += 1;
  });
  return out;
}

// This week so far (Monday to now) against the same stretch of last week.
export function weekCompare(rows, field, keep = () => true) {
  const now = new Date();
  const mon = new Date(now); mon.setHours(0, 0, 0, 0); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
  const lastMon = new Date(mon); lastMon.setDate(lastMon.getDate() - 7);
  const lastNow = new Date(now); lastNow.setDate(lastNow.getDate() - 7);
  let thisWeek = 0, lastWeek = 0;
  (rows || []).forEach(r => {
    if (!r || !r[field] || !keep(r)) return;
    const v = String(r[field]);
    const d = new Date(v.length === 10 ? v + 'T12:00:00' : v);
    if (d >= mon && d <= now) thisWeek++;
    else if (d >= lastMon && d <= lastNow) lastWeek++;
  });
  return { thisWeek, lastWeek, delta: thisWeek - lastWeek };
}

// Progress ring for the Executive "Today" panel.
export function Ring({ pct, size = 108, stroke = 10 }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, pct || 0));
  const col = p >= 100 ? '#34D399' : p >= 50 ? '#FBB040' : '#FB7185';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${(p / 100) * c} ${c}`} style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
}
